import type { DashboardPortfolioReport, PortfolioReport } from '../types';
import { getPortfolioName } from './portfolioName';

export type ViewMode = 'portfolios' | 'exposure' | 'model';

export type ModelOption = { id: string; name: string };

type FilterableReport = Record<string, unknown>;

const matchesCompositePortfolioName = (
  report: FilterableReport,
  lowerSearch: string,
): boolean =>
  getPortfolioName(report as PortfolioReport | DashboardPortfolioReport)
    .toLowerCase()
    .includes(lowerSearch);

export function matchesPortfolioSearch(
  report: FilterableReport,
  lowerSearch: string,
): boolean {
  if (report.modelPortfolioType === 'GOAL') {
    const object = report.object as
      | {
          name?: string;
          user?: {
            firstName?: string;
            lastName?: string;
            entityName?: string;
          };
          householdClientGroup?: { name?: string };
        }
      | undefined;
    const goalName = object?.name?.toLowerCase() || '';
    const userName =
      `${object?.user?.firstName || ''} ${object?.user?.lastName || ''}`.toLowerCase();
    const entityName = object?.user?.entityName?.toLowerCase() || '';
    const household = object?.householdClientGroup?.name?.toLowerCase() || '';
    return (
      goalName.includes(lowerSearch) ||
      userName.includes(lowerSearch) ||
      entityName.includes(lowerSearch) ||
      household.includes(lowerSearch) ||
      matchesCompositePortfolioName(report, lowerSearch)
    );
  }

  const object = report.object as
    | {
        account?: {
          type?: string;
          user?: {
            firstName?: string;
            lastName?: string;
            entityName?: string;
          };
        };
        goal?: {
          name?: string;
          householdClientGroup?: { name?: string };
        };
      }
    | undefined;
  const user = object?.account?.user;
  const fullName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.toLowerCase();
  const entityName = user?.entityName?.toLowerCase() || '';
  const accountType = object?.account?.type?.toLowerCase() || '';
  const goalName = object?.goal?.name?.toLowerCase() || '';
  const household =
    object?.goal?.householdClientGroup?.name?.toLowerCase() || '';
  return (
    fullName.includes(lowerSearch) ||
    entityName.includes(lowerSearch) ||
    accountType.includes(lowerSearch) ||
    goalName.includes(lowerSearch) ||
    household.includes(lowerSearch) ||
    matchesCompositePortfolioName(report, lowerSearch)
  );
}

export function filterPortfolioReports({
  reports,
  search,
}: {
  reports: FilterableReport[];
  search: string;
}): FilterableReport[] {
  if (!search) return reports;

  const lowerSearch = search.toLowerCase();
  return reports.filter((report) =>
    matchesPortfolioSearch(report, lowerSearch),
  );
}

const VIEW_OWN_SEARCH_FILTERS: {
  debouncedSearch: string;
  modelFilter: string[];
} = { debouncedSearch: '', modelFilter: [] };

export function getPortfoliosListFilters(
  viewMode: ViewMode,
  filters: { debouncedSearch: string; modelFilter: string[] },
) {
  if (viewMode === 'model' || viewMode === 'exposure') {
    return VIEW_OWN_SEARCH_FILTERS;
  }

  return filters;
}

export function resolvePortfoliosListContext(
  viewMode: ViewMode,
  filters: { debouncedSearch: string; modelFilter: string[] },
): {
  debouncedSearch: string;
  modelFilter: string[];
  isClientFilter: boolean;
} {
  const activeFilters = getPortfoliosListFilters(viewMode, filters);
  return {
    ...activeFilters,
    isClientFilter: getPortfoliosListClientFilter(viewMode, activeFilters),
  };
}

export function getPortfoliosListClientFilter(
  viewMode: ViewMode,
  _filters: { debouncedSearch: string },
): boolean {
  return viewMode === 'model';
}

export function getSearchPlaceholder(
  viewMode: ViewMode,
): 'searchPortfolios' | 'searchTickers' | 'searchModels' {
  switch (viewMode) {
    case 'portfolios':
      return 'searchPortfolios';
    case 'exposure':
      return 'searchTickers';
    case 'model':
      return 'searchModels';
  }
}
